/*
 * Copyright (c) 2023. Bolmate B.V.
 */
import './style/productCosts.scss';

import {
    AwesomeIcon, ChangeFunction, ChangeKeyFunction, CloseButton, GroupedSelectItem, IndexedObject, Link, Loader,
    OrnamentShape, preventDefault, SelectItemShape, voidFunction, WithBackground, DVRDGroupedSelect
} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {FocusEventHandler, MouseEventHandler, useMemo} from 'react';
import {FBBItem, Fulfilment, ProductCosts, SalesDashboardProduct, SendCostType} from "../../util/interfaces";
import {BolmateButton, BolmateInput, BolmateNumberInput, BolmateSelect, DvrdDatePicker} from "../controls";
import {parseMoney} from "../../util/utils";
import classNames from 'classnames';

interface Props {
    onBack: MouseEventHandler;
    onClose: MouseEventHandler;
    onSelectProductCosts: (productCosts: ProductCosts) => MouseEventHandler;
    onChangeProductCosts: ChangeKeyFunction<keyof ProductCosts>;
    onSubmit: MouseEventHandler;
    onAddProductCosts: MouseEventHandler;
    onDeleteProductCosts: (productCosts: ProductCosts) => MouseEventHandler;
    onChangeMiscCost: (index: number, key: 'label' | 'value', per: 'product' | 'order') => ChangeFunction;
    onAddMiscCost: (per: 'product' | 'order') => MouseEventHandler;
    onSelectFBBItem: ChangeFunction;
    onDeleteMiscCost: (index: number, per: 'product' | 'order') => MouseEventHandler;
    onChangeFulfilment: ChangeFunction<Fulfilment>;
    onChangeListTab: (fulfilment: Fulfilment) => MouseEventHandler;
    onBlurCurrency: (key: keyof ProductCosts) => FocusEventHandler;
    productCostsList: Array<ProductCosts>;
    active: boolean;
    selectedProductCosts: ProductCosts | null;
    loading: boolean;
    fbbItems: Record<string, Array<FBBItem>>;
    product: SalesDashboardProduct | null;
    listTab: Fulfilment;
}

function dateToString(date: string | null): string {
    if (!date) return '-';
    return new IDate(date).format('DD MMM YYYY');
}

function getTotalMiscCosts(costsObject: IndexedObject<number>): number {
    return Object.values(costsObject).reduce((prev: number, current: number) => prev + current, 0);
}

const fulfilmentItems: Array<SelectItemShape> = [
    {value: Fulfilment.FBR, label: 'Eigen levering'},
    {value: Fulfilment.FBB, label: 'LVB'},
];
const sendCostTypeItems: Array<SelectItemShape> = [
    {value: SendCostType.ORDER, label: 'Order'},
    {value: SendCostType.PRODUCT, label: 'Product'},
]
const euroOrnament: OrnamentShape = {element: <span>&euro;</span>};
const inputProps = {placeholder: '0,00'};

export default function ProductCostsPopup(props: Props) {
    const {
        active, productCostsList, onChangeProductCosts, onSelectProductCosts, selectedProductCosts, loading, onBack,
        onClose, onSubmit, onAddProductCosts, onDeleteProductCosts, onChangeMiscCost, onAddMiscCost, onSelectFBBItem,
        fbbItems, onDeleteMiscCost, product, onChangeFulfilment, onChangeListTab, listTab, onBlurCurrency
    } = props;
    const fbbSelectItems: Array<GroupedSelectItem> = useMemo(() => {
        const groupedItems: Array<GroupedSelectItem> = [];
        for (const [key, items] of Object.entries(fbbItems)) {
            if (key === '') groupedItems.push(...items)
            else if (key === 'low_price') groupedItems.push({label: 'Prijs lager dan \u20ac16,-', children: items});
            else if (key === 'normal_price') groupedItems.push({label: 'Prijs vanaf \u20ac16,-', children: items});
        }
        return groupedItems;
    }, [fbbItems]);

    function fbbFromCosts(fulfilmentCosts: number, sendCosts: number): FBBItem | undefined {
        for (const items of Object.values(fbbItems)) {
            const item = items.find(
                (item: FBBItem) => item.fulfilmentCosts === fulfilmentCosts && item.sendCosts === sendCosts);
            if (item) return item;
        }
        return undefined;
    }

    function renderHeader() {
        const title = !!selectedProductCosts ? 'Kosten wijzigen' : 'Productkosten';
        return (
            <div className='header-container'>
                <label className='header-title wrap-text'>{title} van {product?.title}</label>
                <CloseButton onClick={onClose}/>
            </div>
        );
    }

    function renderBody() {
        return !!selectedProductCosts ? renderSelected() : renderList();
    }

    function renderList() {
        return (
            <div className='body-container product-costs-list'>
                {renderListTabs()}
                <BolmateButton onClick={onAddProductCosts} label='+ Kosten toevoegen' className='add-btn' secondary/>
                <div className='product-costs-table'>
                    <Loader active={loading}/>
                    <div className='row default-table-row head'>
                        <label>STARTDATUM</label>
                        <label>EINDDATUM</label>
                        <label>SOORT LEVERING</label>
                        <label>KOSTPRIJS</label>
                        <label>FULFILMENT KOSTEN</label>
                        <label>VERZENDKOSTEN</label>
                        <label>OVERIG PER PRODUCT</label>
                        <label>OVERIG PER ORDER</label>
                        <div/>
                    </div>
                    {productCostsList.map(renderProductCosts)}
                    {!productCostsList.length && <div className='default-table-row empty'>
                        <label className='empty-label'>Je hebt nog geen kosten ingesteld</label>
                    </div>}
                </div>
            </div>
        )
    }

    function renderListTabs() {
        return (
            <div className='list-tabs'>
                {renderListTab(Fulfilment.BOTH)}
                {renderListTab(Fulfilment.FBR)}
                {renderListTab(Fulfilment.FBB)}
            </div>
        )
    }

    function renderListTab(fulfilment: Fulfilment) {
        let label: string;
        if (fulfilment === Fulfilment.FBR) label = 'Eigen verzending';
        else if (fulfilment === Fulfilment.FBB) label = 'LVB';
        else label = 'Alles';
        const active = fulfilment === listTab;
        return (
            <div className={classNames('list-tab', active && 'active')} onClick={onChangeListTab(fulfilment)}>
                <label className='tab-label'>{label}</label>
            </div>
        )
    }

    function renderProductCosts(productCosts: ProductCosts, index: number) {
        const fulfilment = productCosts.fulfilment === Fulfilment.FBB ? 'LVB' : 'Eigen verzending';
        return (
            <div key={index} className='row default-table-row'>
                <label>{dateToString(productCosts.start_date)}</label>
                <label>{dateToString(productCosts.end_date)}</label>
                <label>{fulfilment}</label>
                <label>&euro;{parseMoney(productCosts.cost_price)}</label>
                <label>&euro;{parseMoney(productCosts.fulfilment_cost)}</label>
                <label>&euro;{parseMoney(productCosts.send_cost)}</label>
                <label>&euro;{parseMoney(getTotalMiscCosts(productCosts.misc_costs_per_item))}</label>
                <label>&euro;{parseMoney(getTotalMiscCosts(productCosts.misc_costs_per_order))}</label>
                <div className='product-costs-actions'>
                    <AwesomeIcon name='edit' className='action' onClick={onSelectProductCosts(productCosts)}
                                 title='Wijzigen'/>
                    <AwesomeIcon name='trash-alt' className='action delete' onClick={onDeleteProductCosts(productCosts)}
                                 title='Verwijderen'/>
                </div>
            </div>
        )
    }

    function renderSelected() {
        if (!selectedProductCosts) return null;
        const endDate = !!selectedProductCosts.end_date ? new IDate(selectedProductCosts.end_date) : null;
        return (
            <form className='body-container selected-costs' onSubmit={preventDefault}>
                <Loader active={loading}/>
                <div className='row'>
                    <DvrdDatePicker onChange={onChangeProductCosts('start_date')}
                                    value={new IDate(selectedProductCosts.start_date)} label='Startdatum'
                                    dateFormat='DD MMMM YYYY' closeOnChange/>
                    <DvrdDatePicker onChange={voidFunction} value={endDate} label='Einddatum'
                                    dateFormat='DD MMMM YYYY' placeholder='Geen einddatum' disabled/>
                </div>
                <div className='row'>
                    <BolmateSelect onChange={onChangeFulfilment} value={selectedProductCosts.fulfilment}
                                   items={fulfilmentItems} label='Soort levering'/>
                    <BolmateNumberInput onChange={voidFunction} value={parseMoney(product?.avg_price ?? 0)}
                                        label='Verkoopprijs' ornaments={euroOrnament} disabled/>
                </div>
                {selectedProductCosts.fulfilment === Fulfilment.FBB ? renderFBB() : renderFBR()}
            </form>
        )
    }

    function renderFBR() {
        if (!selectedProductCosts) return null;
        return (
            <>
                <div className='costs-container'>
                    <BolmateNumberInput onChange={onChangeProductCosts('cost_price')}
                                        value={selectedProductCosts.cost_price} label='Kostprijs'
                                        ornaments={euroOrnament} inputProps={inputProps}
                                        onBlur={onBlurCurrency('cost_price')} asCurrency asNumber/>
                    <div className='row'>
                        <BolmateNumberInput onChange={onChangeProductCosts('send_cost')}
                                            value={selectedProductCosts.send_cost} onBlur={onBlurCurrency('send_cost')}
                                            label='Verzendkosten' ornaments={euroOrnament} inputProps={inputProps}
                                            asCurrency asNumber/>
                        <BolmateSelect onChange={onChangeProductCosts('send_cost_type')}
                                       value={selectedProductCosts.send_cost_type} items={sendCostTypeItems}
                                       label='Verzendkosten per'/>
                    </div>
                </div>
                {renderMiscCosts('order', selectedProductCosts.miscCostsPerOrder)}
                {renderMiscCosts('product', selectedProductCosts.miscCostsPerItem)}
            </>
        )
    }

    function renderFBB() {
        if (!selectedProductCosts) return null;
        return (
            <>
                <div className='costs-container'>
                    <div className='row'>
                        <BolmateNumberInput onChange={onChangeProductCosts('cost_price')}
                                            value={selectedProductCosts.cost_price} label='Kostprijs'
                                            ornaments={euroOrnament} inputProps={inputProps}
                                            onBlur={onBlurCurrency('cost_price')} asCurrency asNumber/>
                        <DVRDGroupedSelect onChange={onSelectFBBItem}
                                           value={fbbFromCosts(selectedProductCosts.fulfilment_cost,
                                               selectedProductCosts.send_cost)?.value ?? ''} items={fbbSelectItems}
                                           label='LVB pakketmaat' maxItemsHeight='20rem'/>
                    </div>
                    <BolmateNumberInput onChange={voidFunction} value={parseMoney(selectedProductCosts.fulfilment_cost)}
                                        label='Fulfilmentkosten' ornaments={euroOrnament} inputProps={inputProps}
                                        asCurrency asNumber disabled/>
                    <div className='row'>
                        <BolmateNumberInput onChange={onChangeProductCosts('send_cost')}
                                            value={selectedProductCosts.send_cost} onBlur={onBlurCurrency('send_cost')}
                                            label='Verzendkosten' ornaments={euroOrnament} inputProps={inputProps}
                                            asCurrency asNumber/>
                        <BolmateSelect onChange={onChangeProductCosts('send_cost_type')}
                                       value={selectedProductCosts.send_cost_type} items={sendCostTypeItems}
                                       label='Verzendkosten per'/>
                    </div>
                </div>
                {renderMiscCosts('order', selectedProductCosts.miscCostsPerOrder)}
                {renderMiscCosts('product', selectedProductCosts.miscCostsPerItem)}
            </>
        )
    }

    function renderMiscCosts(per: 'product' | 'order', costs: Array<{label: string; value: number}>) {
        if (!selectedProductCosts) return null;
        return (
            <div className='misc-costs-container'>
                <strong className='misc-label'>Overige kosten per {per}</strong>
                <div className='misc-costs' id='misc-costs-container'>
                    {costs.map(renderMiscCost(per))}
                </div>
                <Link onClick={onAddMiscCost(per)}>+ Kosten per {per} toevoegen</Link>
            </div>
        )
    }

    function renderMiscCost(per: 'product' | 'order') {
        return function renderMiscCost(miscCost: {label: string, value: number}, index: number) {
            return (
                <div key={index} className='misc-cost'>
                    <BolmateInput onChange={onChangeMiscCost(index, 'label', per)} value={miscCost.label}/>
                    <BolmateNumberInput onChange={onChangeMiscCost(index, 'value', per)} value={miscCost.value}
                                        ornaments={euroOrnament} inputProps={inputProps} asCurrency asNumber/>
                    <AwesomeIcon name='times' className='delete-btn' onClick={onDeleteMiscCost(index, per)}/>
                </div>
            )
        }
    }


    function renderFooter() {
        if (!!selectedProductCosts) return (
            <div className='footer-container btn-2'>
                <BolmateButton onClick={onBack} label='Terug naar lijst' disabled={loading} secondary/>
                <BolmateButton onClick={onSubmit} label='Wijzigingen opslaan' disabled={loading}/>
            </div>
        );
        return (
            <div className='footer-container'>
                <BolmateButton onClick={onClose} label='Sluiten' disabled={loading} secondary/>
            </div>
        );
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='product-costs-popup default-popup'>
                {renderHeader()}
                {renderBody()}
                {renderFooter()}
            </div>
        </WithBackground>
    )
}